import { useState, useCallback, useEffect } from 'react';
import { Modal } from '@mui/material';
import { GlobalButton, TextInput, LoaderBackdrop } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import useNotification from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useAssignShippingsToRoute } from '../hooks/useAssignShippingsToRoute';
import { useSearchShipping } from '../hooks/useSearchShipping';
import { shippingNumberSearchSchema } from '../schemas/shippingNumberSearch.schema';

export interface AddShippingsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  routeId: string;
  logisticOperatorId: string;
}

const AddShippingsModal = ({
  open,
  onClose,
  onSuccess,
  routeId,
  logisticOperatorId,
}: AddShippingsModalProps) => {
  const [shippingNumber, setShippingNumber] = useState('');
  const { showNotification } = useNotification();

  const { search, shipping: foundShipping, isLoading: isSearching, reset } =
    useSearchShipping({ logisticOperatorId });

  const handleSuccess = useCallback(() => {
    setShippingNumber('');
    reset();
    onClose();
    onSuccess?.();
  }, [onClose, onSuccess, reset]);

  const { assign, isLoading: isAssigning } = useAssignShippingsToRoute({
    routeId,
    logisticOperatorId,
    onSuccess: handleSuccess,
  });

  useEffect(() => {
    if (!open) {
      setShippingNumber('');
      reset();
    }
  }, [open, reset]);

  const handleSearch = useCallback(async () => {
    const trimmed = shippingNumber.trim();
    const num = parseInt(trimmed, 10);
    const parsed = shippingNumberSearchSchema.safeParse({ number: num });
    if (!parsed.success) {
      const msg = parsed.error.errors[0]?.message ?? 'Ingrese un número de pedido válido';
      showNotification(msg, NotificationSeverity.warning);
      return;
    }
    await search(parsed.data.number);
  }, [shippingNumber, search, showNotification]);

  const handleAdd = useCallback(async () => {
    if (!foundShipping?.number) return;
    const num =
      typeof foundShipping.number === 'string'
        ? parseInt(foundShipping.number, 10)
        : foundShipping.number;
    if (Number.isNaN(num) || num <= 0) return;
    await assign({ shippingNumbers: [num] });
  }, [foundShipping, assign]);

  const handleClose = useCallback(() => {
    if (isAssigning) return;
    setShippingNumber('');
    reset();
    onClose();
  }, [isAssigning, reset, onClose]);

  const handleNumberChange = useCallback((value: string | number) => {
    setShippingNumber(String(value));
  }, []);

  const num = parseInt(shippingNumber.trim(), 10);
  const isValidNumber =
    !Number.isNaN(num) && num > 0 && shippingNumber.trim().length > 0;
  const foundNumber =
    foundShipping?.number != null
      ? typeof foundShipping.number === 'string'
        ? parseInt(foundShipping.number, 10)
        : foundShipping.number
      : null;
  const canAdd = Boolean(foundShipping && foundNumber === num && !Number.isNaN(num));
  const isLoading = isSearching || isAssigning;

  return (
    <Modal open={open} onClose={handleClose}>
      <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md'>
        <div className='relative flex flex-col p-6 gap-4 bg-white outline-none rounded-lg shadow-xl'>
          {isLoading && <LoaderBackdrop />}

          <h2 className='text-lg font-semibold text-gray-800'>
            Agregar pedido a la ruta
          </h2>

          <TextInput
            name='shippingNumber'
            placeholder='Número de pedido'
            value={shippingNumber}
            onChange={handleNumberChange}
            type='number'
            disabled={isLoading}
          />

          {foundShipping && (
            <div className='rounded-lg bg-gray-50 p-3 text-sm text-gray-700'>
              <p className='font-medium'>
                Pedido {foundShipping.number} —{' '}
                {foundShipping.recipient || 'Sin destinatario'}
              </p>
              {foundShipping.destinationAddress && (
                <p className='mt-1 text-gray-600'>
                  {foundShipping.destinationAddress.streetName}{' '}
                  {foundShipping.destinationAddress.doorNumber}
                </p>
              )}
            </div>
          )}

          <div className='flex gap-3 justify-end pt-2'>
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </GlobalButton>
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={handleSearch}
              disabled={!isValidNumber || isLoading}
            >
              {isSearching ? 'Buscando…' : 'Buscar'}
            </GlobalButton>
            <GlobalButton
              variant={GlobalButtonVariant.primaryButton}
              action={handleAdd}
              disabled={!canAdd || isLoading}
            >
              {isAssigning ? 'Agregando…' : 'Agregar'}
            </GlobalButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddShippingsModal;
